import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { allowsSound, passesMuteLevel } from '@bunker/shared/mutes';
import { accountAllows, accountNotifyTopic } from '@bunker/shared/notifyPrefs';
import { currentAccountNotifyPrefs } from '@/lib/accountNotifyPrefs';
import { getChannelLevel } from '@/lib/muteSettings';
import { playMentionSound, playMessageSound } from '@/lib/sounds';
import { isBunkerOnScreen, isDesktopShell } from '@/lib/desktopBridge';
import { shellShowsBunker } from '@/lib/shellVisibility';
import i18n from '@/i18n';

const NOTIFICATIONS_ENABLED_KEY = 'bunker:notifications-enabled';

export function areNotificationsEnabled(): boolean {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  return window.localStorage.getItem(NOTIFICATIONS_ENABLED_KEY) === '1' &&
    window.Notification.permission === 'granted';
}

export async function requestNotifications(): Promise<boolean> {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  const permission = await window.Notification.requestPermission();
  if (permission === 'granted') {
    window.localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, '1');
    return true;
  }
  window.localStorage.removeItem(NOTIFICATIONS_ENABLED_KEY);
  return false;
}

/** Turn the OS banner notifications back off (keeps the browser permission, just
 *  stops us from firing). The delivery path checks areNotificationsEnabled(). */
export function disableNotifications(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(NOTIFICATIONS_ENABLED_KEY);
}

/** True when the device can show OS notifications at all (the Notification API
 *  exists and the user hasn't hard-blocked it in the browser/OS). Electron's
 *  renderer supports this, so it works in the desktop shell too. */
export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window &&
    window.Notification.permission !== 'denied';
}

/** My status is «Не беспокоить»: no sound and no pop-up for messages; the counters still count. */
export function isDoNotDisturb(): boolean {
  return useAuthStore.getState().user?.status === 'DND';
}

export function notifyMessageActivity(input: {
  channelId: string;
  /** The channel's server; absent for a DM or group. */
  guildId?: string;
  authorId: string;
  mentions: string[];
  /** Author of the message this is replying to, if any — clients gate
   *  MENTIONS-level notifications on reply-to-me too, since the UI
   *  promises "только когда упомянули или ответили". */
  replyToAuthorId?: string | null;
  direct: boolean;
}) {
  if (typeof document === 'undefined') return;
  const myId = useAuthStore.getState().user?.id;
  if (!myId || input.authorId === myId) return;
  if (isDoNotDisturb()) return;

  const mentioned = input.mentions.includes(myId);
  const repliedTo = input.replyToAuthorId === myId;
  // Inside the shell's Chat frame the page stays visible while the shell shows another
  // section; the shell says so (lib/shellVisibility.ts).
  const inThisChannelAndFocused =
    document.visibilityState === 'visible' &&
    shellShowsBunker() &&
    useDataStore.getState().currentChannelId === input.channelId;

  // Per-channel / per-server mute level, same rule as the badge and the push.
  const guildId = input.direct ? null : (input.guildId ?? null);
  const level = getChannelLevel(input.channelId, guildId);
  const signal = { isDm: input.direct, mentioned, repliedTo };
  if (!passesMuteLevel(level, signal)) return;

  // The Cispracs ID account is a ceiling over the switches here: it can only
  // silence a DM or a mention/reply (lib/accountNotifyPrefs.ts). A plain server
  // message has no account topic and is not affected.
  const prefs = currentAccountNotifyPrefs();
  const topic = accountNotifyTopic(signal);
  const soundAllowed = allowsSound(level) && accountAllows(prefs, topic, 'sound');

  // Soft in-app ping for ANY message that gets past mute gating —
  // unless the user picked ALL_SILENT (visual on, sound off), the account
  // turned the sound off for this topic, or the user is already focused on
  // this channel.
  // A message addressed to me gets its own, brighter cue.
  if (!inThisChannelAndFocused && soundAllowed) {
    if (mentioned || repliedTo || input.direct) playMentionSound();
    else playMessageSound();
  }

  // One pop-up per message (lib/dmToast.ts messageCardSurface): Desktop draws its own card,
  // the shell pops up for its Chat frame, and an app on screen shows the in-app card. The OS
  // banner is only for a plain browser tab in the background.
  if (isDesktopShell() || !shellShowsBunker() || isBunkerOnScreen()) return;
  if (!areNotificationsEnabled()) return;
  if (!accountAllows(prefs, topic, 'popup')) return;

  // Title accurately reflects what happened (Codex review #3): previously
  // every non-DM activity said "Вас упомянули" even on routine messages
  // that passed through ALL/ALL_SILENT gating, which was misleading.
  const title = input.direct
    ? i18n.t('lib2.notify.newDirect')
    : mentioned
      ? i18n.t('lib2.notify.mentioned')
      : repliedTo
        ? i18n.t('lib2.notify.reply')
        : i18n.t('lib2.notify.newInChannel');
  const body = resolveChannelLabel(input.channelId, guildId);
  const notification = new window.Notification(title, {
    body,
    tag: `bunker:${input.channelId}`,
    // ALL_SILENT: the banner appears, the OS sound channel stays quiet.
    silent: !soundAllowed,
  });
  notification.onclick = () => {
    window.focus();
    const state = useDataStore.getState();
    if (guildId) {
      state.selectGuild(guildId);
      state.selectChannel(input.channelId);
    } else if (state.dmChannels[input.channelId]) {
      state.selectDirectChannel(input.channelId);
    }
    notification.close();
  };
}

function resolveChannelLabel(channelId: string, guildId: string | null): string {
  const state = useDataStore.getState();
  if (guildId) {
    const guild = state.guilds[guildId];
    const channel = guild?.channels.find((item) => item.id === channelId);
    return guild && channel ? `${guild.name} / ${channel.name ?? i18n.t('lib2.notify.channel')}` : 'Bunker';
  }
  const dm = state.dmChannels[channelId];
  if (!dm) return 'Bunker';
  const meId = useAuthStore.getState().user?.id;
  if (dm.type === 'GROUP_DM') {
    const fallbackName = dm.directParticipants
      .filter((participant) => participant.userId !== meId)
      .slice(0, 3)
      .map((participant) => participant.user.displayName || participant.user.username)
      .join(', ');
    return dm.name || fallbackName || i18n.t('lib2.notify.group');
  }
  const other = dm.directParticipants.find((participant) => participant.userId !== meId);
  return other?.user.displayName || other?.user.username || i18n.t('lib2.notify.directMessages');
}
